import { useState, useEffect, useRef } from "react";
import {
	CoreIconButton,
	CoreMenuButton,
	CoreMenuItem,
	CoreModal,
	CoreSpinner,
	CoreToastContainer,
	coreToast,
} from "@verity/components/core";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { VideoPlaybackAction } from "../videoplayer/ProctoringResultView";
import styles from "./SessionNoteRow.module.css";
import i18n from "../../../../i18n";
import { SessionNote } from "../videoplayer/ProctoringVideoPlayer";
import { formatEventTimestamp } from "../../ProctoringResultsPage/ProctoringEventsTable";

export function SessionNoteRow<S extends SessionNote>({
	sessionNote,
	thumbnails,
	offsetGetter,
	dispatch,
	editNote,
	deleteNote,
	hideOptions,
	duration,
}: {
	sessionNote: S;
	thumbnails: {
		startTime?: number;
		endTime?: number;
		canvas?: HTMLCanvasElement | null;
	}[];
	offsetGetter: number;
	dispatch: React.Dispatch<VideoPlaybackAction>;
	editNote?: (noteId: S["id"], content: string) => Promise<boolean>;
	deleteNote?: (noteId: S["id"]) => Promise<boolean>;
	hideOptions?: boolean;
	duration: number;
}) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const textFieldRef = useRef<HTMLTextAreaElement>(null);

	const [editing, setEditing] = useState(false);
	const [editValue, setEditValue] = useState(sessionNote.content);
	const [menuOpen, setMenuOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [removeParentHover, setRemoveParentHover] = useState(false);

	const [waiting, setWaiting] = useState(false);

	useEffect(() => {
		const savedCanvas = thumbnails.find(
			(x) => (x?.startTime ?? -1) <= sessionNote.time && (x?.endTime ?? -1) >= sessionNote.time,
		)?.canvas;
		if (savedCanvas && savedCanvas.width > 0 && savedCanvas.height > 0 && canvasRef?.current) {
			canvasRef.current.width = savedCanvas.width;
			canvasRef.current.height = savedCanvas.height;
			canvasRef.current.getContext("2d")?.drawImage(savedCanvas, 0, 0, savedCanvas.width, savedCanvas.height);
		}
	}, [sessionNote.time, thumbnails]);

	const menuItemProps = {
		style: {
			fontSize: "14px",
		},
	};

	const thumbnailTimeStamp = formatEventTimestamp(sessionNote.time * 1000 - offsetGetter, duration);

	return (
		<>
			<div className={styles["session-note-row"]}>
				<div
					className={`${styles["session-note-row-button"]} ${removeParentHover || editing ? "" : styles["session-note-hover"]}`}
					onClick={() => dispatch({ type: "skip", time: sessionNote.time * 1000 })}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							dispatch({ type: "skip", time: sessionNote.time * 1000 });
						}
					}}
					aria-label={`${String(
						i18n.t("result.components.studentSelection.sessionNotesTSX.sessionNoteAriaLabel", {
							time: thumbnailTimeStamp,
						}),
					)}  ${sessionNote.content}`}
					tabIndex={0}
					role="button"
				>
					<div
						style={{ backgroundColor: thumbnails?.[1]?.canvas ? "black" : "grey" }}
						className={styles["note-thumbnail"]}
					>
						<canvas
							ref={canvasRef}
							style={{ minHeight: "100%", minWidth: "100%", aspectRatio: "auto", borderRadius: "10px" }}
						/>
						<div className={styles["note-thumbnail-overlay"]}>
							<PlayArrowRoundedIcon style={{ marginBottom: "-4px" }} fontSize="large" />
							{thumbnailTimeStamp}
						</div>
					</div>
					{editing && editNote ? (
						<div
							style={{
								position: "relative",
								display: "flex",
								alignItems: "center",
								flex: 1,
								overflow: "clip",
							}}
							onClick={(e) => e.stopPropagation()}
						>
							<div className={styles["note-textarea-wrapper"]}>
								<textarea
									ref={textFieldRef}
									className={styles["note-textarea"]}
									onKeyDown={(e) => {
										e.stopPropagation();
										if (e.key === "Escape") {
											setEditing(false);
										}
									}}
									onChange={(e) => setEditValue(e.target.value)}
									value={editValue}
									disabled={waiting}
									aria-label={i18n.t("result.components.studentSelection.sessionNotesTSX.textAreaAriaLabel")}
								/>
							</div>
							<div className={styles["note-edit-buttons-holder"]}>
								<CoreIconButton
									variant="plain"
									style={{ borderColor: "#0b6f85" }}
									className={styles["note-edit-button"]}
									aria-label="Save edits"
									onClick={() => {
										setWaiting(true);
										editNote(sessionNote.id, editValue)
											.then(() => {
												setEditing(false);
											})
											.catch(() => {
												coreToast.error("Failed to save edits to note.");
											})
											.finally(() => {
												setWaiting(false);
											});
									}}
									disabled={waiting}
								>
									{waiting ? (
										<CoreSpinner color="secondary" size="14px" />
									) : (
										<CheckRoundedIcon style={{ width: "20px", height: "20px", color: "#0b6f85" }} />
									)}
								</CoreIconButton>
								<CoreIconButton
									variant="plain"
									style={{ borderColor: "#bf0033" }}
									className={styles["note-edit-button"]}
									aria-label="Cancel edits"
									onClick={() => setEditing(false)}
									disabled={waiting}
								>
									{waiting ? (
										<CoreSpinner color="red" size="14px" />
									) : (
										<ClearRoundedIcon style={{ width: "20px", height: "20px", color: "#bf0033" }} />
									)}
								</CoreIconButton>
							</div>
						</div>
					) : (
						<div className={styles["note-content"]}>{sessionNote.content}</div>
					)}
				</div>
				<div className={styles["session-note-actions"]}>
					{hideOptions || (!editNote && !deleteNote) ? null : (
						<div className={styles["menu-button-wrapper"]}>
							<CoreMenuButton
								aria-label={i18n.t("result.components.studentSelection.sessionNotesTSX.menuAriaLabel")}
								menuShowing={menuOpen}
								setMenuShowing={setMenuOpen}
								slotProps={{
									menu: {
										anchorOrigin: { vertical: "bottom", horizontal: "left" },
										transformOrigin: { vertical: "top", horizontal: "left" },
									},
								}}
								size="medium"
								onMouseEnter={() => setRemoveParentHover(true)}
								onMouseLeave={() => setRemoveParentHover(false)}
							>
								{editNote && (
									<CoreMenuItem
										onClick={(e) => {
											e.stopPropagation();
											setEditValue(sessionNote.content);
											setEditing(true);
											setMenuOpen(false);
											setTimeout(() => {
												textFieldRef.current?.focus();
											}, 0);
										}}
										{...menuItemProps}
									>
										{i18n.t("result.components.studentSelection.sessionNotesTSX.edit")}
									</CoreMenuItem>
								)}
								{deleteNote && (
									<CoreMenuItem
										onClick={(e) => {
											e.stopPropagation();
											setMenuOpen(false);
											setDeleteModalOpen(true);
										}}
										{...menuItemProps}
									>
										{i18n.t("result.components.studentSelection.sessionNotesTSX.delete")}
									</CoreMenuItem>
								)}
							</CoreMenuButton>
						</div>
					)}
				</div>
			</div>
			<CoreModal
				open={deleteModalOpen}
				onClose={() => setDeleteModalOpen(false)}
				title={i18n.t("result.components.studentSelection.sessionNotesTSX.deleteNote")}
				buttons={[
					{
						text: i18n.t("result.components.studentSelection.sessionNotesTSX.no"),
						color: "cancel",
						onClick: () => setDeleteModalOpen(false),
						disabled: waiting,
					},
					{
						text: i18n.t("result.components.studentSelection.sessionNotesTSX.yes"),
						onClick: () => {
							setWaiting(true);
							deleteNote?.(sessionNote.id)
								.then(() => {
									setDeleteModalOpen(false);
									setEditing(false);
								})
								.catch(() => {
									coreToast.error("Failed to delete note.");
								})
								.finally(() => {
									setWaiting(false);
								});
						},
						disabled: waiting,
					},
				]}
				disableTopCloseButton={waiting}
			>
				<div>{i18n.t("result.components.studentSelection.sessionNotesTSX.sureDelete")}</div>
				<CoreToastContainer />
			</CoreModal>
		</>
	);
}
